import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { ShieldCheck, Shield, Lock, FileText, Cloud, Database, CheckCircle2, HelpCircle, Upload, RefreshCcw, Trash2, Cpu } from 'lucide-react';
import SeoHead from '../components/SeoHead';
import Header from '../components/Header';
import GlobalFooter from '../components/GlobalFooter';
// PromptBar import removed — GlobalChatSystem will be added later
import { certificationBadges } from '../data/securityBadges';
const COLORS = {
  brandDark: 'var(--brand-black)',
  secondaryDark: 'var(--brand-charcoal)',
  accentGold: 'var(--brand-gold)',
  hoverGold: 'var(--brand-gold-hover)',
  textSecondary: 'var(--brand-text-secondary)',
  softWhite: 'var(--brand-canvas)',
  border: 'var(--brand-line)'
};

const SECURITY_SECTIONS = [
  { id: 'hero', label: 'overview' },
  { id: 'certifications', label: 'certifications' },
  { id: 'data-path', label: 'data path' },
  { id: 'no-training', label: 'no training' },
  { id: 'compliance', label: 'compliance' },
  { id: 'faq', label: 'faq' },
  { id: 'contact', label: 'resources' }
] as const;

const heroIntro = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px) translateY(-50%);
  }
  to {
    opacity: 1;
    transform: translateX(0) translateY(-50%);
  }
`;

const PageWrapper = styled.main`
  background: linear-gradient(180deg, #ffffff 0%, ${COLORS.softWhite} 100%);
  min-height: 100vh;
  padding: clamp(2.5rem, 5vw, 3.5rem) clamp(1rem, 4vw, 2.5rem) clamp(4rem, 6vw, 6rem);
  padding-bottom: clamp(4rem, 6vw, 6rem);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  color: ${COLORS.brandDark};
  display: flex;
  flex-direction: column;
  gap: clamp(2.5rem, 5vw, 3.5rem);
  box-sizing: border-box;
  a {
    color: ${COLORS.accentGold};
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }

  @media (prefers-color-scheme: dark) {
    background: radial-gradient(circle at top, rgba(198, 167, 94, 0.15), rgba(3, 8, 18, 0.95));
    color: #f5f7fb;
    a {
      color: ${COLORS.accentGold};
    }
  }
`;


const SecurityLayout = styled.div`
  width: 100%;
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(1.75rem, 3.5vw, 3rem);
  width: min(1200px, 100%);
  margin: clamp(0.75rem, 2vw, 1.5rem) auto 0;
`;

const NavigationBar = styled.div`
  position: fixed;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(15px);
  box-shadow: 0 clamp(4px, 1vw, 6px) clamp(12px, 3vw, 16px) rgba(0, 0, 0, 0.08);
  padding: clamp(1rem, 2vw, 1.25rem) clamp(0.5rem, 1vw, 0.65rem);
  z-index: 1200;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: clamp(8px, 1.5vw, 10px) 0 0 clamp(8px, 1.5vw, 10px);
  transition: all 0.3s ease;
  overflow: hidden;
  width: clamp(120px, 15vw, 140px);
  animation: ${slideIn} 0.8s ease-out;

  &:hover {
    box-shadow: 0 clamp(6px, 1.5vw, 8px) clamp(18px, 4vw, 24px) rgba(198, 167, 94, 0.08);
  }

  @media (max-width: 1400px) {
    display: none;
  }

  @media (prefers-color-scheme: dark) {
    background: rgba(12, 20, 35, 0.9);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.6);
  }
`;

const NavContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(0.5rem, 1.5vw, 0.75rem);
  width: 100%;

  @media (max-width: 1400px) {
    flex-direction: row;
    align-items: center;
    gap: clamp(0.375rem, 1vw, 0.5rem);
    min-width: max-content;
  }
`;

const NavLink = styled.button`
  color: ${COLORS.textSecondary};
  font-weight: 500;
  background: transparent;
  border: none;
  text-decoration: none;
  padding: clamp(0.625rem, 1.5vw, 0.75rem) clamp(1rem, 2.5vw, 1.25rem);
  border-radius: clamp(6px, 1vw, 8px);
  transition: all 0.3s ease;
  white-space: nowrap;
  cursor: pointer;
  position: relative;
  width: 100%;
  text-align: center;
  font-size: clamp(0.8rem, 1.5vw, 0.9rem);
  letter-spacing: 0.5px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: lowercase;

  @media (hover: hover) {
    &:hover {
      color: ${COLORS.accentGold};
      background: rgba(198, 167, 94, 0.05);
      transform: translateY(clamp(-1px, -0.25vw, -2px));
    }
  }

  @media (hover: none) {
    &:active {
      background: rgba(198, 167, 94, 0.08);
      transform: scale(0.98);
    }
  }

  &.active {
    color: white;
    background: linear-gradient(135deg, ${COLORS.brandDark}, ${COLORS.secondaryDark});
    box-shadow: 0 clamp(3px, 0.75vw, 4px) clamp(8px, 2vw, 10px) rgba(26, 26, 26, 0.15);

    &:hover {
      transform: translateY(clamp(-1px, -0.25vw, -2px));
      box-shadow: 0 clamp(4px, 1vw, 6px) clamp(10px, 2.5vw, 12px) rgba(26, 26, 26, 0.2);
    }
  }

  @media (max-width: 1400px) {
    width: auto;
    padding: clamp(0.5rem, 1.25vw, 0.625rem) clamp(0.75rem, 2vw, 1rem);
    font-size: clamp(0.75rem, 1.75vw, 0.85rem);
    border-radius: clamp(5px, 1vw, 6px);
    min-width: clamp(80px, 20vw, 90px);
  }

  @media (max-width: 600px) {
    padding: clamp(0.5rem, 1vw, 0.575rem) clamp(0.65rem, 1.75vw, 0.875rem);
    font-size: clamp(0.7rem, 1.5vw, 0.8rem);
    min-width: clamp(70px, 18vw, 85px);
  }

  @media (prefers-color-scheme: dark) {
    color: rgba(245, 247, 251, 0.8);
  }
`;

const Section = styled.section`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: clamp(2rem, 3vw, 3rem);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 30px 80px rgba(15, 27, 44, 0.07);
  border: 1px solid ${COLORS.border};

  @media (prefers-color-scheme: dark) {
    background: rgba(8, 15, 27, 0.85);
    border: 1px solid rgba(255, 255, 255, 0.08);
    box-shadow: 0 20px 70px rgba(0, 0, 0, 0.55);
  }
`;

const HeroSection = styled(Section)`
  --hero-glow-x: 55%;
  --hero-glow-y: 35%;
  --hero-tilt-x: 0;
  --hero-tilt-y: 0;
  display: block;
  text-align: left;
  background: linear-gradient(135deg, ${COLORS.brandDark}, ${COLORS.secondaryDark});
  color: #ffffff;
  position: relative;
  overflow: hidden;
  padding: clamp(2.4rem, 4vw, 3.6rem) clamp(1.5rem, 4vw, 3.25rem);
  animation: ${heroIntro} 0.9s ease-out both;
  margin-top: clamp(1rem, 2.5vw, 1.75rem);
  background-image: radial-gradient(
      circle at var(--hero-glow-x, 55%) var(--hero-glow-y, 35%),
      rgba(198, 167, 94, 0.18),
      transparent 60%
    ),
    linear-gradient(135deg, ${COLORS.brandDark}, ${COLORS.secondaryDark});
  background-blend-mode: screen, normal;
  transition: background 0.5s ease, box-shadow 0.5s ease, transform 0.45s ease;
  will-change: background-position, transform;

  &::before {
    content: '';
    position: absolute;
    inset: 12% 10% auto 10%;
    height: 55%;
    background: radial-gradient(circle at center, rgba(255, 255, 255, 0.15), transparent 65%);
    mix-blend-mode: screen;
    opacity: 0.6;
    pointer-events: none;
    transition: opacity 0.4s ease;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.32), transparent 55%),
      radial-gradient(circle at 78% 30%, rgba(255, 255, 255, 0.14), transparent 60%);
    pointer-events: none;
    transition: opacity 0.4s ease;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      box-shadow: 0 40px 120px rgba(4, 15, 32, 0.35);
    }

    &:hover::before {
      opacity: 0.95;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`;

const HeroContent = styled.header`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: clamp(1.5rem, 3vw, 2.5rem);
  align-items: stretch;
  width: 100%;
  isolation: isolate;
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: clamp(1.35rem, 3.5vw, 2.6rem);
  align-items: center;
  position: relative;
  z-index: 1;

  @media (min-width: 992px) {
    grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);
    gap: clamp(1.75rem, 3vw, 3.25rem);
  }

  @media (min-width: 1280px) {
    grid-template-columns: minmax(0, 1.2fr) minmax(0, 0.8fr);
  }
`;

const HeroTextColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(1rem, 1.75vw, 1.35rem);
  text-align: left;
  position: relative;
  padding: clamp(0.85rem, 1.8vw, 1.35rem);
  max-width: min(760px, 100%);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  transition: transform 0.45s ease, background 0.45s ease, box-shadow 0.45s ease, border-color 0.45s ease;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-6px);
      box-shadow: 0 25px 80px rgba(4, 15, 32, 0.35);
      background: rgba(255, 255, 255, 0.08);
      border-color: rgba(255, 255, 255, 0.28);
    }
  }

  @media (max-width: 768px) {
    max-width: 100%;
    padding: clamp(0.75rem, 4vw, 1rem);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const HeroHeadline = styled.h1`
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: clamp(1.85rem, 3.8vw, 3.35rem);
  font-weight: 800;
  margin: 0;
  letter-spacing: -0.035em;
  text-transform: lowercase;
  line-height: 1.08;
  color: #ffffff;
  background-image: linear-gradient(120deg, #ffffff 0%, #F5F0E1 45%, ${COLORS.accentGold} 100%);
  background-size: 200% 200%;
  position: relative;
  transition: transform 0.35s ease, text-shadow 0.35s ease, background-position 1.6s ease;
  max-width: 32ch;
  text-wrap: balance;

  @supports (-webkit-background-clip: text) or (background-clip: text) {
    color: transparent;
    -webkit-background-clip: text;
    background-clip: text;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-4px);
      text-shadow: 0 20px 45px rgba(5, 12, 35, 0.35);
      background-position: 100% 50%;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const HeroSubheading = styled.p`
  font-size: clamp(1rem, 1.9vw, 1.35rem);
  margin: 0;
  line-height: 1.5;
  max-width: 46ch;
  position: relative;
  opacity: 0.92;
  text-wrap: pretty;
  transition: color 0.35s ease, transform 0.35s ease;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-2px);
      color: #ffffff;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const HeroBadgesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6rem 0.85rem;
  justify-content: flex-start;
  position: relative;
  padding: 0.4rem 0 0.2rem;

  @media (max-width: 520px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const HeroBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.28);
  text-transform: lowercase;
  font-size: clamp(0.65rem, 1.5vw, 0.78rem);
  letter-spacing: 0.12em;
  font-weight: 600;
  color: #ffffff;
  position: relative;
  cursor: default;
  transition: transform 0.35s ease, background 0.35s ease, border-color 0.35s ease, box-shadow 0.35s ease;
  isolation: isolate;

  @media (max-width: 520px) {
    width: 100%;
    justify-content: flex-start;
  }

  &::after {
    content: attr(data-tooltip);
    position: absolute;
    left: 50%;
    bottom: calc(100% + 10px);
    transform: translate(-50%, 8px);
    padding: 0.45rem 0.6rem;
    border-radius: 12px;
    background: rgba(6, 12, 24, 0.85);
    color: #F5F0E1;
    font-size: 0.72rem;
    letter-spacing: 0.04em;
    white-space: normal;
    text-transform: none;
    width: max-content;
    max-width: 220px;
    opacity: 0;
    pointer-events: none;
    box-shadow: 0 12px 28px rgba(4, 15, 32, 0.45);
    transition: opacity 0.3s ease, transform 0.3s ease;
    line-height: 1.35;
  }

  @media (hover: hover) and (pointer: fine) {
    cursor: help;

    &:hover,
    &:focus-visible {
      transform: translateY(-4px) scale(1.02);
      background: rgba(255, 255, 255, 0.2);
      border-color: rgba(255, 255, 255, 0.45);
      box-shadow: 0 20px 48px rgba(4, 15, 32, 0.35);
    }

    &:hover::after,
    &:focus-visible::after {
      opacity: 1;
      transform: translate(-50%, -4px);
    }
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.65);
    outline-offset: 4px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const HeroBadgeImage = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.08);
  padding: 0.08rem;
  transition: transform 0.35s ease, filter 0.35s ease;

  ${HeroBadge}:hover &,
  ${HeroBadge}:focus-visible & {
    transform: scale(1.05);
    filter: drop-shadow(0 0 12px rgba(255, 255, 255, 0.35));
  }
`;

const HeroBadgeLabel = styled.span`
  white-space: nowrap;
  letter-spacing: inherit;
  font-size: inherit;
`;

const HeroBodyCopy = styled.p`
  font-size: clamp(0.95rem, 1.5vw, 1.1rem);
  margin: 0;
  line-height: 1.55;
  max-width: 52ch;
  opacity: 0.9;
  position: relative;
  padding-right: clamp(0rem, 4vw, 2rem);
  text-wrap: pretty;
  transition: color 0.35s ease, transform 0.35s ease;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: #ffffff;
      transform: translateY(-2px);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: flex-start;
`;

const illustrationReveal = keyframes`
  from {
    opacity: 0;
    transform: scale(0.94) translateY(14px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

const floatPulse = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
  100% { transform: translateY(0); }
`;

const pulseGlow = keyframes`
  0% { opacity: 0.65; }
  50% { opacity: 1; }
  100% { opacity: 0.65; }
`;

const IllustrationWrapper = styled.div`
  width: 100%;
  max-width: clamp(320px, 38vw, 500px);
  margin: clamp(0.5rem, 2vw, 1rem) auto;
  display: flex;
  justify-content: center;
  perspective: 1400px;

  @media (min-width: 992px) {
    justify-self: flex-end;
    margin: 0 0 0 auto;
  }

  @media (max-width: 600px) {
    max-width: min(360px, 100%);
  }
`;

const IllustrationCard = styled.div`
  background: rgba(10, 22, 43, 0.35);
  border-radius: 32px;
  padding: clamp(1.1rem, 2.4vw, 1.75rem);
  backdrop-filter: blur(6px);
  border: 1px solid rgba(255, 255, 255, 0.18);
  animation: ${illustrationReveal} 0.9s ease-out both;
  box-shadow: 0 25px 60px rgba(6, 12, 24, 0.4);
  transform-style: preserve-3d;
  will-change: transform;
  transition: transform 0.45s ease, box-shadow 0.45s ease, border-color 0.45s ease, background 0.45s ease;
  transform: perspective(1400px)
    rotateX(calc(var(--hero-tilt-y, 0) * -12deg))
    rotateY(calc(var(--hero-tilt-x, 0) * 14deg))
    translate3d(
      calc(var(--hero-tilt-x, 0) * 24px),
      calc(var(--hero-tilt-y, 0) * 18px),
      0
    );
  pointer-events: none;

  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 28px;
  }

  @media (hover: hover) and (pointer: fine) {
    pointer-events: auto;

    &:hover {
      box-shadow: 0 35px 90px rgba(2, 8, 18, 0.5);
      border-color: rgba(255, 255, 255, 0.3);
      background: rgba(10, 22, 43, 0.45);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    animation: none;
    transform: none;
  }
`;

const IllustrationSvg = styled.svg`
  display: block;
  width: 100%;
  height: auto;
  max-height: 360px;
  margin: 0 auto;

  .flow {
    animation: ${floatPulse} 5s ease-in-out infinite;
    transform-origin: center;
  }

  .shield {
    animation: ${pulseGlow} 4s ease-in-out infinite;
  }
`;

const HeroButton = styled.a<{ $variant?: 'primary' | 'ghost' }>`
  text-decoration: none;
  padding: 0.85rem 1.8rem;
  border-radius: 999px;
  font-weight: 600;
  border: 1px solid rgba(255, 255, 255, 0.4);
  color: #ffffff;
  text-transform: lowercase;
  transition: transform 0.2s ease, background 0.2s ease;
  ${({ $variant }) =>
    $variant === 'ghost'
      ? css`
          background: transparent;
        `
      : css`
          background: rgba(255, 255, 255, 0.15);
        `}

  &:hover {
    transform: translateY(-2px);
  }
`;

const heroBodyCopy =
  'prest0 powers your workflows with zero data training, layered encryption, and human-auditable agents so every team member can move fast without giving up trust.';

type HeroBadgeMeta = {
  id: string;
  label: string;
  image: string;
  alt: string;
  tooltip: string;
};

const heroBadges: HeroBadgeMeta[] = [
  {
    id: 'soc2',
    label: 'soc 2 type ii infrastructure',
    image: '/badge-soc2-type2.webp',
    alt: 'SOC 2 TYPE II badge',
    tooltip: 'the infrastructure we run on is SOC 2 Type II certified by Google Cloud. Prest0 has not completed an audit of its own yet.'
  },
  {
    id: 'iso',
    label: 'iso/iec 27001 infrastructure',
    image: '/badge-iso-27001.webp',
    alt: 'ISO/IEC 27001 badge',
    tooltip: 'the infrastructure we run on is certified to ISO/IEC 27001 by Google Cloud. our own program follows the same playbook; it is not separately certified.'
  },
  {
    id: 'zero-training',
    label: 'zero ai training',
    image: '/badge-zero-training.webp',
    alt: 'Zero AI training badge',
    tooltip: 'enterprise ai endpoints do not train on or retain your prompts \u2014 a contractual guarantee.'
  }
];

const CloudShieldIconWrapper = styled.span<{ $size: number }>`
  position: relative;
  width: ${({ $size }) => `${$size}px`};
  height: ${({ $size }) => `${$size}px`};
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const CloudShieldOverlay = styled.span`
  position: absolute;
  bottom: -2px;
  right: -4px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${COLORS.accentGold};
  color: #ffffff;
  display: grid;
  place-items: center;
  border: 2px solid rgba(8, 15, 27, 0.35);
`;

const CloudShieldGlyph: React.FC<{ size?: number }> = ({ size = 32 }) => (
  <CloudShieldIconWrapper $size={size}>
    <Cloud size={size} />
    <CloudShieldOverlay>
      <ShieldCheck size={size * 0.5} />
    </CloudShieldOverlay>
  </CloudShieldIconWrapper>
);

const ZeroTrainingBadgeSvg = () => (
  <svg viewBox="0 0 120 120" width="120" height="120" aria-hidden="true" focusable="false">
    <circle cx="60" cy="60" r="48" fill="none" stroke="var(--brand-gold)" strokeWidth="4" />
    <line x1="30" y1="90" x2="90" y2="30" stroke="var(--brand-gold)" strokeWidth="6" strokeLinecap="round" />
    <text x="60" y="70" textAnchor="middle" fontSize="16" fontWeight="700" fill="var(--brand-black)" fontFamily="'Inter',-apple-system,BlinkMacSystemFont,sans-serif">
      ZERO TRAINING
    </text>
  </svg>
);

const SecurityIllustration = () => (
  <IllustrationWrapper aria-hidden="true">
    <IllustrationCard>
      <IllustrationSvg viewBox="0 0 360 280" role="presentation" focusable="false">
        <defs>
          <linearGradient id="flowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.6)" />
            <stop offset="100%" stopColor="var(--brand-gold)" />
          </linearGradient>
          <linearGradient id="shieldGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#D4B872" />
            <stop offset="100%" stopColor="var(--brand-gold)" />
          </linearGradient>
          <linearGradient id="shieldCore" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#E8D5A3" />
            <stop offset="100%" stopColor="var(--brand-gold-hover)" />
          </linearGradient>
        </defs>

        <rect x="10" y="10" width="340" height="260" rx="28" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.2)" />
        <g className="flow" fill="none" stroke="url(#flowGradient)" strokeWidth="4" strokeLinecap="round">
          <path d="M35 70H170C205 70 205 115 245 115H325" />
          <path d="M35 120H140C175 120 180 150 215 150H325" opacity="0.9" />
          <path d="M35 180H110C150 180 160 215 205 215H325" opacity="0.75" />
          <path d="M35 220H150C200 220 210 250 260 250H325" opacity="0.6" />
          <polygon points="325,115 340,110 340,120" fill="var(--brand-gold)" />
          <polygon points="325,150 340,145 340,155" fill="var(--brand-gold)" opacity="0.9" />
          <polygon points="325,215 340,210 340,220" fill="var(--brand-gold)" opacity="0.75" />
          <polygon points="325,250 340,245 340,255" fill="var(--brand-gold)" opacity="0.6" />
        </g>

        {[{ x: 150, y: 60 }, { x: 205, y: 140 }, { x: 165, y: 205 }].map((lock, idx) => (
          <g key={idx} transform={`translate(${lock.x}, ${lock.y})`} opacity={0.9}>
            <rect x="0" y="12" width="34" height="24" rx="6" fill="rgba(198,167,94,0.7)" stroke="rgba(255,255,255,0.3)" />
            <path d="M8 12V8c0-5 4-9 9-9s9 4 9 9v4" stroke="rgba(255,255,255,0.85)" strokeWidth="2" fill="none" />
            <circle cx="17" cy="24" r="3" fill="#ffffff" />
          </g>
        ))}

        <g className="shield">
          <path d="M255 40l65 22v62c0 44-30 94-65 109c-35-15-65-65-65-109V62z" fill="url(#shieldGradient)" stroke="rgba(255,255,255,0.4)" strokeWidth="3" />
          <path d="M255 60l45 16v44c0 32-20 68-45 80c-25-12-45-48-45-80V76z" fill="url(#shieldCore)" />
          <path d="M255 90c15 0 27 12 27 27c0 21-27 42-27 42s-27-21-27-42c0-15 12-27 27-27z" fill="none" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M255 102c-8 0-14 6-14 14v10c0 8 6 14 14 14s14-6 14-14v-10c0-8-6-14-14-14z" fill="#ffffff" opacity="0.9" />
          <rect x="245" y="126" width="20" height="16" rx="4" fill="var(--brand-black)" />
        </g>

        <g opacity="0.55">
          <circle cx="80" cy="90" r="8" fill="var(--brand-gold)" />
          <circle cx="90" cy="200" r="6" fill="var(--brand-gold-hover)" />
          <circle cx="200" cy="70" r="5" fill="var(--brand-gold)" />
        </g>
      </IllustrationSvg>
    </IllustrationCard>
  </IllustrationWrapper>
);

const MemoizedSecurityIllustration = React.memo(SecurityIllustration);

const SectionHeading = styled.div`
  margin-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const Eyebrow = styled.p`
  font-size: 0.9rem;
  letter-spacing: 0.2em;
  text-transform: lowercase;
  color: ${COLORS.accentGold};
  margin: 0;

  @media (prefers-color-scheme: dark) {
    color: rgba(255, 255, 255, 0.7);
  }
`;

const Title = styled.h2`
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  margin: 0;
  text-transform: lowercase;
  color: ${COLORS.brandDark};

  @media (prefers-color-scheme: dark) {
    color: #e4ebff;
  }
`;

const Body = styled.p`
  margin: 0;
  font-size: 1rem;
  color: ${COLORS.textSecondary};
  line-height: 1.6;
  text-transform: lowercase;

  @media (prefers-color-scheme: dark) {
    color: rgba(244, 244, 244, 0.78);
  }
`;

const NoTrainingTitle = styled(Title)`
  text-transform: none;
`;

const NoTrainingBody = styled(Body)`
  text-transform: none;
`;

const ComplianceTitle = styled(Title)`
  text-transform: none;
`;

const ComplianceBody = styled(Body)`
  text-transform: none;
`;

const SnapshotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: clamp(0.85rem, 2vw, 1.35rem);

  @media (min-width: 1280px) {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
`;

const SnapshotTitle = styled.h3`
  font-family: 'Satoshi', -apple-system, BlinkMacSystemFont, sans-serif;
  margin: 0;
  font-size: 0.95rem;
  text-transform: none;
  letter-spacing: 0.01em;
  color: ${COLORS.brandDark};

  @media (prefers-color-scheme: dark) {
    color: #E8D5A3;
  }
`;

const SnapshotCopy = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.4;
  color: ${COLORS.textSecondary};

  @media (prefers-color-scheme: dark) {
    color: rgba(245, 247, 251, 0.82);
  }
`;

const SnapshotIcon = styled.span`
  width: 48px;
  height: 48px;
  border-radius: 16px;
  background: rgba(198, 167, 94, 0.1);
  color: ${COLORS.accentGold};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.25s ease, color 0.25s ease;

  @media (prefers-color-scheme: dark) {
    background: rgba(255, 255, 255, 0.12);
    color: #E8D5A3;
  }
`;

const SnapshotContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;


const SnapshotCard = styled.article`
  padding: 1.2rem;
  border-radius: 16px;
  background: rgba(198, 167, 94, 0.04);
  display: flex;
  align-items: center;
  gap: 1rem;
  min-height: 150px;
  border: 1px solid rgba(198, 167, 94, 0.15);
  transition: background 0.25s ease, transform 0.25s ease, border-color 0.25s ease;

  &:hover,
  &:focus-within {
    background: ${COLORS.brandDark};
    transform: translateY(-4px);
    border-color: ${COLORS.accentGold};
  }

  &:hover ${SnapshotTitle},
  &:focus-within ${SnapshotTitle},
  &:hover ${SnapshotCopy},
  &:focus-within ${SnapshotCopy} {
    color: #ffffff;
  }

  &:hover ${SnapshotIcon},
  &:focus-within ${SnapshotIcon} {
    background: rgba(255, 255, 255, 0.25);
    color: #ffffff;
  }

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    min-height: auto;
  }

  @media (prefers-color-scheme: dark) {
    background: rgba(255, 255, 255, 0.07);
    border-color: rgba(255, 255, 255, 0.12);

    &:hover,
    &:focus-within {
      background: rgba(255, 255, 255, 0.18);
    }
  }
`;

const DataFlowContainer = styled.div`
  position: relative;
  background: linear-gradient(120deg, rgba(26, 26, 26, 0.06), rgba(198, 167, 94, 0.08));
  border: 1px solid rgba(198, 167, 94, 0.12);
  border-radius: 32px;
  padding: clamp(1rem, 2vw, 1.6rem);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at top right, rgba(255, 255, 255, 0.18), transparent 55%);
    pointer-events: none;
  }
`;

const DataFlowGrid = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: clamp(1rem, 2vw, 1.8rem);
  align-items: stretch;
  justify-items: stretch;
  grid-auto-rows: 1fr;
  padding: clamp(0.35rem, 1.5vw, 0.85rem) 0;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (min-width: 1440px) {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
`;

const DataFlowIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(6px);
  transition: transform 0.35s ease, box-shadow 0.35s ease;
`;

const DataFlowNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: rgba(5, 10, 20, 0.25);
  color: #ffffff;
  font-weight: 700;
  font-size: 1rem;
  letter-spacing: 0.05em;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.12);
`;

const DataFlowTitle = styled.h3`
  margin: 0;
  font-size: 1.15rem;
  letter-spacing: -0.01em;
  color: #ffffff;
  text-transform: none;
  text-align: center;
  width: 100%;
  line-height: 1.2;
`;

const DataFlowSubtitle = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.85rem;
  border-radius: 999px;
  font-size: 0.85rem;
  letter-spacing: 0.05em;
  color: #ffffff;
  background: rgba(255, 255, 255, 0.18);
  text-transform: none;
  border: 1px solid rgba(255, 255, 255, 0.3);
  text-align: center;
  justify-content: center;
`;

const DataFlowDetail = styled.p`
  margin: 0;
  font-size: 0.92rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.45;
  text-transform: none;
  max-width: 320px;
  text-align: center;
  margin-left: auto;
  margin-right: auto;
`;

const DataFlowMeta = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  justify-content: center;
`;

const DataFlowStep = styled.article<{ $gradient: string; $visible: boolean }>`
  --df-tilt-x: 0deg;
  --df-tilt-y: 0deg;
  --df-spot-x: 50%;
  --df-spot-y: 50%;
  --df-spot-opacity: 0;
  min-height: auto;
  border-radius: 24px;
  padding: clamp(1.2rem, 2.2vw, 1.75rem);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: clamp(0.95rem, 2vw, 1.25rem);
  background: ${({ $gradient }) => $gradient};
  color: #ffffff;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.18);
  box-shadow: 0 18px 40px rgba(2, 10, 20, 0.25);
  backdrop-filter: blur(4px);
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: ${({ $visible }) =>
      $visible ? 'translateY(0)' : 'translateY(16px)'}
    rotateX(var(--df-tilt-y, 0deg))
    rotateY(var(--df-tilt-x, 0deg));
  transition: opacity 0.45s ease, transform 0.45s ease, box-shadow 0.45s ease,
    border-color 0.45s ease, background 0.45s ease;
  transition-delay: ${({ $visible }) => ($visible ? 'calc(var(--order, 0) * 70ms)' : '0ms')};
  cursor: default;
  align-items: center;
  text-align: center;
  width: 100%;
  max-width: 360px;
  margin: 0 auto;
  align-items: center;
  text-align: center;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: radial-gradient(
      circle at var(--df-spot-x, 50%) var(--df-spot-y, 50%),
      rgba(255, 255, 255, 0.35),
      transparent 60%
    );
    opacity: var(--df-spot-opacity, 0);
    pointer-events: none;
    transition: opacity 0.3s ease;
    z-index: 0;
  }

  ${DataFlowIcon} {
    position: relative;
    z-index: 1;
  }

  ${DataFlowNumber} {
    transition: transform 0.35s ease, background 0.35s ease, box-shadow 0.35s ease;
  }

  ${DataFlowTitle} {
    transition: color 0.3s ease;
  }

  ${DataFlowSubtitle} {
    transition: background 0.3s ease, color 0.3s ease, border-color 0.3s ease;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover ${DataFlowIcon},
    &:focus-visible ${DataFlowIcon} {
      transform: scale(1.07);
      box-shadow: 0 12px 28px rgba(0, 0, 0, 0.35);
    }

    &:hover ${DataFlowNumber},
    &:focus-visible ${DataFlowNumber} {
      background: rgba(255, 255, 255, 0.25);
      box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.4);
      transform: translateY(-2px);
    }

    &:hover ${DataFlowTitle},
    &:focus-visible ${DataFlowTitle} {
      color: #F5F0E1;
    }

    &:hover ${DataFlowSubtitle},
    &:focus-visible ${DataFlowSubtitle} {
      background: rgba(255, 255, 255, 0.32);
      color: #0c1a2f;
      border-color: rgba(255, 255, 255, 0.55);
    }
  }

  &::after {
    content: attr(data-tooltip);
    position: absolute;
    left: 50%;
    bottom: calc(100% + 12px);
    transform: translate(-50%, 8px) scale(0.98);
    padding: 0.65rem 0.85rem;
    border-radius: 16px;
    background: rgba(2, 6, 14, 0.92);
    color: #F5F0E1;
    font-size: 0.85rem;
    line-height: 1.4;
    width: min(260px, 90vw);
    opacity: 0;
    pointer-events: none;
    box-shadow: 0 18px 36px rgba(0, 0, 0, 0.4);
    transition: opacity 0.25s ease, transform 0.25s ease;
    z-index: 10;
    text-transform: none;
    letter-spacing: 0.01em;
  }

  @media (min-width: 768px) {
    min-height: 220px;
  }

  @media (hover: hover) and (pointer: fine) {
    cursor: pointer;

    &:hover,
    &:focus-visible {
      box-shadow: 0 30px 65px rgba(2, 8, 18, 0.35);
      border-color: rgba(255, 255, 255, 0.32);
      background: ${({ $gradient }) => $gradient}, rgba(8, 20, 38, 0.35);
      background-blend-mode: screen;
      --df-spot-opacity: 0.45;
    }

    &:hover::after,
    &:focus-visible::after {
      opacity: 1;
      transform: translate(-50%, -4px) scale(1);
    }
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.6);
    outline-offset: 4px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: opacity 0.45s ease, transform 0.45s ease;
    transform: ${({ $visible }) => ($visible ? 'translateY(0)' : 'translateY(16px)')};
  }
`;

const DataFlowHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  width: 100%;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.85rem;
  border-radius: 999px;
  background: rgba(198, 167, 94, 0.1);
  color: ${COLORS.accentGold};
  font-weight: 600;
  text-transform: lowercase;
  font-size: 0.9rem;
`;

const ComparisonTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-transform: none;
  background: #ffffff;
  border-radius: 18px;
  overflow: hidden;

  thead {
    background: ${COLORS.brandDark};
    color: #ffffff;

    th {
      color: #ffffff;
      letter-spacing: 0.02em;
      font-size: 0.95rem;
      font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, sans-serif;
    }
  }

  th,
  td {
    padding: 1rem 1.25rem;
    border-bottom: 1px solid ${COLORS.border};
  }

  tbody tr:nth-child(even) {
    background: ${COLORS.softWhite};
  }

  tbody tr:nth-child(odd) {
    background: #ffffff;
  }

  td {
    font-size: 0.95rem;
    color: ${COLORS.textSecondary};
    vertical-align: top;
  }

  @media (prefers-color-scheme: dark) {
    background: rgba(8, 15, 27, 0.85);

    thead {
      background: rgba(26, 26, 26, 0.95);
    }

    tbody tr:nth-child(even) {
      background: rgba(255, 255, 255, 0.04);
    }

    tbody tr:nth-child(odd) {
      background: rgba(255, 255, 255, 0.08);
    }

    th,
    td {
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    }

    td {
      color: rgba(245, 247, 251, 0.86);
    }
  }

  @media (max-width: 1100px) {
    thead {
      display: none;
    }

    tbody tr {
      display: flex;
      flex-direction: column;
      border: 1px solid ${COLORS.border};
      border-radius: 16px;
      margin-bottom: 1rem;
      box-shadow: 0 10px 25px rgba(15, 27, 44, 0.08);
      overflow: hidden;
      background: #ffffff;
    }

    tbody td {
      display: flex;
      flex-direction: column;
      gap: 0.4rem;
      border-bottom: none;
      padding: 0.75rem 1rem;
    }

    tbody td::before {
      content: attr(data-label);
      font-weight: 600;
      color: ${COLORS.brandDark};
      text-transform: none;
      font-size: 0.85rem;
      letter-spacing: 0.08em;
    }

    @media (prefers-color-scheme: dark) {
      tbody tr {
        border-color: rgba(255, 255, 255, 0.12);
        background: rgba(255, 255, 255, 0.08);
      }

      tbody td::before {
        color: #E8D5A3;
      }
    }
  }
`;

const Callout = styled.article`
  background: linear-gradient(135deg, rgba(26, 26, 26, 0.04), rgba(198, 167, 94, 0.08));
  border-radius: 20px;
  padding: clamp(1.5rem, 3vw, 2.5rem);
  border: 1px solid rgba(198, 167, 94, 0.18);
  text-align: left;

  @media (prefers-color-scheme: dark) {
    background: linear-gradient(135deg, rgba(26, 26, 26, 0.24), rgba(198, 167, 94, 0.12));
    border-color: rgba(255, 255, 255, 0.12);
  }
`;

const BadgeGrid = styled.div`
  --badge-glow-x: 50%;
  --badge-glow-y: 48%;
  --badge-glow-opacity: 0.22;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: clamp(1rem, 2vw, 1.5rem);
  justify-items: center;
  position: relative;
  padding: clamp(0.5rem, 2vw, 1rem);
  border-radius: 28px;
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    inset: clamp(0.35rem, 1.5vw, 0.75rem);
    border-radius: inherit;
    background: radial-gradient(
      circle at var(--badge-glow-x, 50%) var(--badge-glow-y, 50%),
      rgba(255, 255, 255, var(--badge-glow-opacity, 0.22)),
      transparent 55%
    );
    pointer-events: none;
    transition: opacity 0.3s ease;
    z-index: 0;
  }
`;

const BadgeCard = styled.div<{ $accent: string }>`
  width: 100%;
  max-width: 210px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  border-radius: 18px;
  border: 1px solid rgba(198, 167, 94, 0.16);
  background: rgba(255, 255, 255, 0.92);
  padding: 1.3rem 1rem;
  min-height: 165px;
  text-align: center;
  box-shadow: 0 12px 32px rgba(15, 27, 44, 0.08);
  position: relative;
  overflow: hidden;
  cursor: default;
  transition: transform 0.45s ease, border-color 0.35s ease, background 0.35s ease, box-shadow 0.35s ease;
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at var(--badge-glow-x, 50%) var(--badge-glow-y, 50%), ${({ $accent }) =>
  $accent}, transparent 65%);
    opacity: 0.35;
    transition: opacity 0.35s ease;
    pointer-events: none;
    z-index: -1;
  }

  @media (prefers-color-scheme: dark) {
    background: rgba(6, 12, 24, 0.8);
    border-color: rgba(255, 255, 255, 0.15);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);
  }

  @media (hover: hover) and (pointer: fine) {
    cursor: pointer;

    &:hover,
    &:focus-visible {
      transform: translateY(-8px) scale(1.02);
      border-color: rgba(255, 255, 255, 0.55);
      background: rgba(255, 255, 255, 0.98);
      box-shadow: 0 30px 60px rgba(6, 12, 24, 0.25);
    }

    &:hover::before,
    &:focus-visible::before {
      opacity: 0.75;
    }
  }

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.7);
    outline-offset: 4px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const BadgeTooltip = styled.span`
  position: absolute;
  left: 50%;
  bottom: calc(100% + 12px);
  transform: translate(-50%, 10px) scale(0.95);
  opacity: 0;
  pointer-events: none;
  padding: 0.6rem 0.85rem;
  border-radius: 14px;
  background: rgba(6, 12, 24, 0.92);
  color: #F5F0E1;
  font-size: 0.78rem;
  line-height: 1.45;
  letter-spacing: 0.04em;
  text-transform: none;
  width: min(220px, 90vw);
  box-shadow: 0 18px 32px rgba(3, 9, 20, 0.35);
  transition: opacity 0.3s ease, transform 0.3s ease;
  text-align: left;
  z-index: 12;

  ${BadgeCard}:hover &,
  ${BadgeCard}:focus-visible & {
    opacity: 1;
    transform: translate(-50%, -4px) scale(1);
  }

  @media (prefers-color-scheme: dark) {
    background: rgba(230, 247, 255, 0.08);
    color: #f3fcff;
    box-shadow: 0 20px 34px rgba(0, 0, 0, 0.45);
  }
`;

const BadgeImage = styled.img`
  width: 95px;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 6px 18px rgba(15, 27, 44, 0.18));
  transition: transform 0.35s ease, filter 0.35s ease;

  &[data-size='large'] {
    width: 130px;
  }

  ${BadgeCard}:hover &,
  ${BadgeCard}:focus-visible & {
    transform: translateY(-4px) scale(1.05);
    filter: drop-shadow(0 18px 28px rgba(10, 22, 43, 0.35));
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const BadgeLabel = styled.span`
  font-weight: 700;
  font-size: 0.85rem;
  letter-spacing: 0.06em;
  text-transform: lowercase;
  color: rgba(7, 10, 18, 0.95);
  transition: color 0.3s ease;

  ${BadgeCard}:hover &,
  ${BadgeCard}:focus-visible & {
    color: rgba(3, 6, 12, 0.98);
  }

  @media (prefers-color-scheme: dark) {
    color: rgba(238, 242, 255, 0.9);

    ${BadgeCard}:hover &,
    ${BadgeCard}:focus-visible & {
      color: rgba(3, 6, 12, 0.98);
    }
  }
`;

const AccordionItem = styled.article<{ $open: boolean }>`
  border-bottom: 1px solid rgba(15, 27, 44, 0.1);
  padding: 1rem 0;

  button {
    width: 100%;
    background: none;
    border: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 1rem;
    text-align: left;
    cursor: pointer;
    text-transform: none;
    color: inherit;
  }

  p {
    margin: ${({ $open }) => ($open ? '0.75rem 0 0' : '0')};
    display: ${({ $open }) => ($open ? 'block' : 'none')};
    color: ${COLORS.textSecondary};

    @media (prefers-color-scheme: dark) {
      color: rgba(245, 247, 251, 0.82);
    }
  }

  @media (prefers-color-scheme: dark) {
    border-bottom-color: rgba(255, 255, 255, 0.08);
  }
`;

const FaqGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FaqGroupTitle = styled.h3`
  font-family: 'Satoshi', -apple-system, BlinkMacSystemFont, sans-serif;
  margin: 0 0 0.75rem;
  font-size: 1rem;
  text-transform: none;
  color: ${COLORS.accentGold};

  @media (prefers-color-scheme: dark) {
    color: #E8D5A3;
  }
`;

const ContactCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: ${COLORS.softWhite};
  border-radius: 18px;
  padding: 1.5rem;
  border: 1px solid ${COLORS.border};

  @media (prefers-color-scheme: dark) {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.12);
  }
`;

const ContactRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

const ContactLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  color: ${COLORS.brandDark};
  font-weight: 600;
  text-transform: lowercase;

  @media (prefers-color-scheme: dark) {
    color: #E8D5A3;
  }
`;

const NoTrainingSection = styled(Section)`
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid ${COLORS.border};
  color: inherit;

  @media (prefers-color-scheme: dark) {
    background: rgba(8, 15, 27, 0.9);
    border: 1px solid rgba(255, 255, 255, 0.08);
  }
`;

const NoTrainingHighlight = styled.span`
  font-weight: 700;
  color: ${COLORS.accentGold};
`;

const NoTrainingTable = styled(ComparisonTable)`
  background: transparent;

  tbody tr:nth-child(even) {
    background: rgba(255, 255, 255, 0.7);
  }

  tbody tr:nth-child(odd) {
    background: rgba(255, 255, 255, 0.95);
  }

  @media (prefers-color-scheme: dark) {
    tbody tr:nth-child(even) {
      background: rgba(255, 255, 255, 0.06);
    }

    tbody tr:nth-child(odd) {
      background: rgba(255, 255, 255, 0.08);
    }
  }
`;

const PolicyLink = styled.a`
  color: ${COLORS.accentGold};
  text-decoration: underline;
  text-transform: lowercase;
  font-weight: 600;

  &:hover {
    opacity: 0.8;
  }
`;

type SnapshotItem = {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
};

const SnapshotItems: SnapshotItem[] = [
  {
    id: 'aes',
    title: 'AES-256 encryption',
    description: 'bank-grade AES-256 keeps records unreadable even if storage is touched.',
    icon: Lock
  },
  {
    id: 'zero-training',
    title: 'zero AI training',
    description: 'data never feeds model training; dedicated enterprise AI endpoints enforce it.',
    icon: ShieldCheck
  },
  {
    id: 'oauth',
    title: 'OAuth 2.0 authentication',
    description: 'adaptive OAuth 2.0 with mfa and device checks gates every session.',
    icon: CheckCircle2
  },
  {
    id: 'audits',
    title: 'audit ready',
    description: 'immutable logs show who touched which record and when.',
    icon: FileText
  },
  {
    id: 'gcp',
    title: 'google cloud infrastructure',
    description: 'runs on GCP with SOC 2 TYPE II and ISO 27001 controls already in place.',
    icon: CloudShieldGlyph
  }
];

const dataFlowSteps = [
  {
    id: 'submit',
    title: 'you submit data',
    subtitle: 'TLS 1.3 ingress',
    detail: 'TLS 1.3 locks the first hop—hover for the chain.',
    tooltip: 'strict transport security, forward secrecy, and certificate pinning block downgrade attempts before any record leaves your browser.',
    icon: Upload,
    gradient: 'linear-gradient(135deg, rgba(26,26,26,0.75), rgba(43,43,43,0.85))'
  },
  {
    id: 'auth',
    title: 'authentication',
    subtitle: 'adaptive OAuth 2.0',
    detail: 'tokens only mint after adaptive auth clears every risk check.',
    tooltip: 'device posture, behavioral risk scoring, MFA, and short-lived OAuth scopes stop reused passwords from granting data access.',
    icon: Shield,
    gradient: 'linear-gradient(135deg, rgba(26,26,26,0.8), rgba(43,43,43,0.9))'
  },
  {
    id: 'storage',
    title: 'encrypted storage',
    subtitle: 'GCP AES-256 at rest',
    detail: 'AES-256 storage keys wrap every record even off disk.',
    tooltip: 'Firestore encrypts every document with per-chunk keys rotated by Cloud KMS, so a stolen snapshot still reads as noise.',
    icon: Database,
    gradient: 'linear-gradient(135deg, rgba(26,26,26,0.82), rgba(198,167,94,0.35))'
  },
  {
    id: 'processing',
    title: 'AI processing',
    subtitle: 'policy-aware pipeline',
    detail: 'prompts go to enterprise model endpoints under a contractual no-training guarantee.',
    // 2026-09-11: this tooltip claimed redaction and DLP tagging fire before
    // forwarding. Neither exists — no prompt-redaction code is in the tree, the
    // FAQ below says so outright ('we do not run a redaction layer over your
    // prompts'), and the generated site digest the guest assistant is grounded
    // on says 'No prompt-redaction layer.' A security claim contradicted by our
    // own two other surfaces is the worst kind. Corrected to what is true.
    tooltip: 'the permission system decides who may send what before anything leaves the workspace, and every governed action lands in the append-only audit feed. we do not redact or DLP-tag your prompts \u2014 what you send is what the model sees.',
    icon: Cpu,
    gradient: 'linear-gradient(135deg, rgba(43,43,43,0.85), rgba(198,167,94,0.45))'
  },
  {
    id: 'results',
    title: 'results returned',
    subtitle: 'HTTPS return path',
    detail: 'signed TLS responses carry audit markers home.',
    tooltip: 'every answer streams back over TLS 1.3 with response hashing and an audit id so you can trace who asked, when, and what returned.',
    icon: RefreshCcw,
    gradient: 'linear-gradient(135deg, rgba(198,167,94,0.5), rgba(182,146,76,0.7))'
  },
  {
    id: 'retention',
    title: 'you control retention',
    subtitle: 'configurable deletion policy',
    detail: 'retention timers delete everywhere on your clock.',
    tooltip: 'on request we delete Firestore docs, stored attachments and AI transcripts, and confirm when it is done. self-service windows are on the roadmap.',
    icon: Trash2,
    gradient: 'linear-gradient(135deg, rgba(182,146,76,0.65), rgba(198,167,94,0.85))'
  }
] as const;

const complianceRows = [
  {
    requirement: 'encryption at rest',
    minimum: 'required for sensitive data',
    implementation: 'AES-256 with managed key rotation',
    status: '✅ exceeded'
  },
  {
    requirement: 'encryption in transit',
    minimum: 'TLS required',
    implementation: 'TLS 1.3 over HTTPS',
    status: '✅ exceeded'
  },
  {
    requirement: 'access controls',
    minimum: 'restrict unauthorized access',
    implementation: 'role-based access control + scoped API keys',
    status: '✅ exceeded'
  },
  {
    requirement: 'no training on data',
    minimum: 'contractually required',
    implementation: 'enterprise AI endpoints with binding no-training terms',
    status: '✅ compliant'
  },
  {
    requirement: 'breach notification',
    minimum: '48–72 hours',
    implementation: '48-hour SLA with tracked postmortems',
    status: '✅ compliant'
  },
  {
    requirement: 'data retention policy',
    minimum: 'document and enforce windows',
    implementation: 'documented schedules; deletion on request. per-workspace timers are on the roadmap, not shipped',
    status: '✅ compliant'
  },
  {
    requirement: 'third-party audit',
    minimum: 'SOC or ISO recommended',
    implementation: 'SOC 2 TYPE II + ISO 27001 inherited from Google Cloud; Prest0 has no audit of its own yet, and penetration testing is planned',
    status: '✅ compliant'
  }
] as const;

const faqGroups = [
  {
    id: 'security',
    title: 'security essentials',
    items: [
      {
        id: 'encryption-proof',
        q: 'how do you verify encryption end to end?',
        a: 'all ingress runs over TLS 1.3, storage is AES-256 with managed keys, and we share live dashboards plus signed attestations so reviewers can see enforcement in real time.'
      },
      {
        id: 'audit-access',
        q: 'can external assessors review your evidence pack?',
        a: 'yes. under NDA we share Google Cloud\u2019s SOC 2 TYPE II and ISO/IEC 27001 reports for the infrastructure, our own control mappings against them, and audit logs. we do not yet have a Prest0 SOC 2 report or a completed penetration test to hand over \u2014 when we do, they go in this pack.'
      },
      {
        id: 'least-privilege',
        q: 'who can access production data?',
        a: 'only just-in-time responders with hardware MFA. elevations auto-expire and every touch point is mirrored into tamper-evident logs.'
      }
    ]
  },
  {
    id: 'data',
    title: 'data protection',
    items: [
      {
        id: 'vendor-boundaries',
        q: 'how do you keep external ai vendors isolated?',
        a: 'requests route to enterprise model endpoints under contractual no-training and no-retention terms, and those obligations pass down to every provider we use. we do not run a redaction layer over your prompts \u2014 what you send is what the model sees, which is why the permission system decides who can send what in the first place.'
      },
      {
        id: 'retention-controls',
        q: 'can we align retention with our policies?',
        a: 'we delete your data on request, across Firestore, stored files and transcripts, and confirm when it is done. self-service retention timers and automated deletion receipts are on the roadmap \u2014 today this is an operational process, not a setting in the product.'
      }
    ]
  }
] as const;

const firstFaqId = faqGroups[0]?.items[0]?.id ?? null;

const Security: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>(SECURITY_SECTIONS[0].id);
  const [openFaqId, setOpenFaqId] = useState<string | null>(firstFaqId);
  const [isDataPathVisible, setIsDataPathVisible] = useState(false);
  const securitySchema = useMemo(
    () =>
      JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'prest0',
        url: 'https://www.prest0.com',
        sameAs: [],
        contactPoint: [],
        hasCredential: []
      }),
    []
  );
  const heroSectionRef = useRef<HTMLElement | null>(null);
  const certificationsGridRef = useRef<HTMLDivElement | null>(null);


  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]?.target?.id) {
          setActiveSection(visible[0].target.id);
        }
      },
      { threshold: 0.2, rootMargin: '-15% 0px -15% 0px' }
    );

    const elements = SECURITY_SECTIONS.map((item) => document.getElementById(item.id)).filter(
      (el): el is HTMLElement => !!el
    );
    elements.forEach((el) => observer.observe(el));
    return () => {
      elements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  useEffect(() => {
    const el = document.getElementById('data-path');
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsDataPathVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);


  useEffect(() => {
    if (typeof window === 'undefined') return;
    const section = heroSectionRef.current;
    if (!section) return;

    let raf: number | null = null;
    let interactionsAttached = false;

    const setProperties = (xPercent: number, yPercent: number, tiltX: number, tiltY: number) => {
      if (raf) {
        cancelAnimationFrame(raf);
      }
      raf = window.requestAnimationFrame(() => {
        section.style.setProperty('--hero-glow-x', `${xPercent}%`);
        section.style.setProperty('--hero-glow-y', `${yPercent}%`);
        section.style.setProperty('--hero-tilt-x', tiltX.toFixed(4));
        section.style.setProperty('--hero-tilt-y', tiltY.toFixed(4));
      });
    };

    const reset = () => setProperties(55, 35, 0, 0);
    const clamp = (value: number) => Math.min(Math.max(value, 0), 1);

    const mediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

    const handlePointerMove = (event: PointerEvent) => {
      const rect = section.getBoundingClientRect();
      const relativeX = (event.clientX - rect.left) / rect.width;
      const relativeY = (event.clientY - rect.top) / rect.height;
      const x = clamp(relativeX);
      const y = clamp(relativeY);
      setProperties(x * 100, y * 100, x - 0.5, y - 0.5);
    };

    const handlePointerLeave = () => {
      reset();
    };

    const attachInteractions = () => {
      if (interactionsAttached) return;
      section.addEventListener('pointermove', handlePointerMove);
      section.addEventListener('pointerleave', handlePointerLeave);
      interactionsAttached = true;
    };

    const detachInteractions = () => {
      if (!interactionsAttached) return;
      section.removeEventListener('pointermove', handlePointerMove);
      section.removeEventListener('pointerleave', handlePointerLeave);
      interactionsAttached = false;
    };

    if (!mediaQuery.matches) {
      reset();
      return () => {
        if (raf) {
          cancelAnimationFrame(raf);
        }
      };
    }

    attachInteractions();
    reset();

    const handleMediaChange = (event: MediaQueryListEvent) => {
      if (event.matches) {
        attachInteractions();
      } else {
        detachInteractions();
        reset();
      }
    };

    const supportsAddEventListener = typeof mediaQuery.addEventListener === 'function';
    if (supportsAddEventListener) {
      mediaQuery.addEventListener('change', handleMediaChange);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(handleMediaChange);
    }

    return () => {
      detachInteractions();
      if (supportsAddEventListener) {
        mediaQuery.removeEventListener('change', handleMediaChange);
      } else if (typeof mediaQuery.removeListener === 'function') {
        mediaQuery.removeListener(handleMediaChange);
      }
      if (raf) {
        cancelAnimationFrame(raf);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const grid = certificationsGridRef.current;
    if (!grid) return;

    let raf: number | null = null;
    let interactionsAttached = false;

    const setProperties = (xPercent: number, yPercent: number, opacity = 0.2) => {
      if (raf) {
        cancelAnimationFrame(raf);
      }
      raf = window.requestAnimationFrame(() => {
        grid.style.setProperty('--badge-glow-x', `${xPercent}%`);
        grid.style.setProperty('--badge-glow-y', `${yPercent}%`);
        grid.style.setProperty('--badge-glow-opacity', opacity.toFixed(3));
      });
    };

    const clamp = (value: number) => Math.min(Math.max(value, 0), 1);
    const mediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

    const handlePointerMove = (event: PointerEvent) => {
      const rect = grid.getBoundingClientRect();
      const x = clamp((event.clientX - rect.left) / rect.width);
      const y = clamp((event.clientY - rect.top) / rect.height);
      setProperties(x * 100, y * 100, 0.32);
    };

    const handlePointerLeave = () => {
      setProperties(50, 48, 0.18);
    };

    const attachInteractions = () => {
      if (interactionsAttached) return;
      grid.addEventListener('pointermove', handlePointerMove);
      grid.addEventListener('pointerleave', handlePointerLeave);
      interactionsAttached = true;
    };

    const detachInteractions = () => {
      if (!interactionsAttached) return;
      grid.removeEventListener('pointermove', handlePointerMove);
      grid.removeEventListener('pointerleave', handlePointerLeave);
      interactionsAttached = false;
    };

    if (mediaQuery.matches) {
      attachInteractions();
      setProperties(50, 48, 0.22);
    } else {
      setProperties(50, 48, 0.12);
    }

    const handleMediaChange = (event: MediaQueryListEvent) => {
      if (event.matches) {
        attachInteractions();
        setProperties(50, 48, 0.22);
      } else {
        detachInteractions();
        setProperties(50, 48, 0.12);
      }
    };

    const supportsAddEventListener = typeof mediaQuery.addEventListener === 'function';
    if (supportsAddEventListener) {
      mediaQuery.addEventListener('change', handleMediaChange);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(handleMediaChange);
    }

    return () => {
      detachInteractions();
      if (supportsAddEventListener) {
        mediaQuery.removeEventListener('change', handleMediaChange);
      } else if (typeof mediaQuery.removeListener === 'function') {
        mediaQuery.removeListener(handleMediaChange);
      }
      if (raf) {
        cancelAnimationFrame(raf);
      }
    };
  }, []);

  const resetDataFlowCard = useCallback((card: HTMLElement) => {
    if (typeof window === 'undefined') return;
    window.requestAnimationFrame(() => {
      card.style.setProperty('--df-tilt-x', '0deg');
      card.style.setProperty('--df-tilt-y', '0deg');
      card.style.setProperty('--df-spot-x', '50%');
      card.style.setProperty('--df-spot-y', '50%');
      card.style.setProperty('--df-spot-opacity', '0');
    });
  }, []);

  const handleDataFlowPointerMove = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (event.pointerType === 'touch') return;
      const card = event.currentTarget as HTMLElement;
      if (!card) return;
      const rect = card.getBoundingClientRect();
      const clamp = (value: number) => Math.min(Math.max(value, 0), 1);
      const xRatio = clamp((event.clientX - rect.left) / rect.width);
      const yRatio = clamp((event.clientY - rect.top) / rect.height);
      const rotateY = (xRatio - 0.5) * 8;
      const rotateX = (0.5 - yRatio) * 6;
      if (typeof window === 'undefined') return;
      window.requestAnimationFrame(() => {
        card.style.setProperty('--df-tilt-x', `${rotateY}deg`);
        card.style.setProperty('--df-tilt-y', `${rotateX}deg`);
        card.style.setProperty('--df-spot-x', `${xRatio * 100}%`);
        card.style.setProperty('--df-spot-y', `${yRatio * 100}%`);
        card.style.setProperty('--df-spot-opacity', '0.55');
      });
    },
    []
  );

  const handleDataFlowPointerLeave = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const card = event.currentTarget as HTMLElement;
      if (!card) return;
      resetDataFlowCard(card);
    },
    [resetDataFlowCard]
  );

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const yOffset = 80;
    const y = el.getBoundingClientRect().top + window.scrollY - yOffset;
    window.scrollTo({ top: y, behavior: 'smooth' });
    setActiveSection(id);
  };

  return (
    <>
      <SeoHead
        title="security | prest0"
        tabTitle="security"
        description="Enterprise compliance for large law firms: SOC 2, data sovereignty, zero-training policy, and end-to-end encrypted legal AI infrastructure."
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: securitySchema }} />
      <Header />
      <PageWrapper>
        <NavigationBar aria-label="security sections navigation">
          <NavContainer>
            {SECURITY_SECTIONS.map((section) => (
              <NavLink
                key={section.id}
                type="button"
                className={activeSection === section.id ? 'active' : ''}
                onClick={() => scrollToSection(section.id)}
              >
                {section.label}
              </NavLink>
            ))}
          </NavContainer>
        </NavigationBar>
        <SecurityLayout>
          <MainContent>
        {/* Hero Section */}
        <HeroSection ref={heroSectionRef} id="hero" as="section" aria-labelledby="security-hero-title">
          <HeroContent>
            <HeroGrid>
              <HeroTextColumn>
                <HeroHeadline id="security-hero-title">enterprise-grade security, built for you</HeroHeadline>
                <HeroSubheading>
                  your work sits on the same google cloud infrastructure the fortune 500 runs on — SOC 2 Type II and ISO 27001 underneath you, without the enterprise price tag on top.
                </HeroSubheading>
                <HeroBadgesRow aria-label="trust badges" role="list">
                  {heroBadges.map((badge) => (
                    <HeroBadge
                      key={badge.id}
                      role="listitem"
                      tabIndex={0}
                      data-tooltip={badge.tooltip}
                      aria-label={`${badge.label} – ${badge.tooltip}`}
                    >
                      <HeroBadgeImage src={badge.image} alt={badge.alt} loading="lazy" />
                      <HeroBadgeLabel>{badge.label}</HeroBadgeLabel>
                    </HeroBadge>
                  ))}
                </HeroBadgesRow>
                <HeroBodyCopy>{heroBodyCopy}</HeroBodyCopy>
              </HeroTextColumn>
              <MemoizedSecurityIllustration />
            </HeroGrid>
          </HeroContent>
        </HeroSection>

        {/* Certifications & Badges Section */}
        <Section id="certifications" aria-labelledby="certifications-title">
          <SectionHeading>
            <Title id="certifications-title">certifications</Title>
          </SectionHeading>
        <BadgeGrid ref={certificationsGridRef} role="list" aria-live="polite">
          {certificationBadges.map((badge) => {
            const tooltipId = `cert-tooltip-${badge.id}`;
            return (
            <BadgeCard
              key={badge.id}
              role="listitem"
              tabIndex={0}
              aria-describedby={tooltipId}
              $accent={badge.accent}
            >
              <BadgeImage
                src={badge.image}
                alt={badge.alt}
                loading="lazy"
                data-size={badge.size}
                data-id={badge.id}
              />
              <BadgeLabel>{badge.label}</BadgeLabel>
              <BadgeTooltip id={tooltipId} role="tooltip">
                {badge.tooltip}
              </BadgeTooltip>
            </BadgeCard>
          );
          })}
        </BadgeGrid>
        </Section>

        {/* Your Data Path Section */}
        <Section id="data-path" aria-labelledby="data-path-title">
          <SectionHeading>
            <Title id="data-path-title">your data path</Title>
            <Body>hover or focus any step to pop out the controls that keep each hop sealed.</Body>
          </SectionHeading>
          <DataFlowContainer>
            <DataFlowGrid role="list">
              {dataFlowSteps.map((step, index) => (
                <DataFlowStep
                  key={step.id}
                  role="listitem"
                  $gradient={step.gradient}
                  $visible={isDataPathVisible}
                  tabIndex={0}
                  data-tooltip={step.tooltip}
                  aria-label={`${step.title}: ${step.detail}. ${step.tooltip}`}
                  onPointerMove={handleDataFlowPointerMove}
                  onPointerLeave={handleDataFlowPointerLeave}
                  style={{ '--order': index } as React.CSSProperties}
                >
                  <DataFlowHeader>
                  <DataFlowMeta>
                    <DataFlowNumber>{index + 1}</DataFlowNumber>
                    <DataFlowIcon>
                      <step.icon size={26} />
                    </DataFlowIcon>
                  </DataFlowMeta>
                  <DataFlowSubtitle>{step.subtitle}</DataFlowSubtitle>
                  </DataFlowHeader>
                  <DataFlowTitle>{step.title}</DataFlowTitle>
                  <DataFlowDetail>{step.detail}</DataFlowDetail>
                </DataFlowStep>
              ))}
            </DataFlowGrid>
          </DataFlowContainer>
        </Section>

        {/* No Training Section */}
        <NoTrainingSection id="no-training" aria-labelledby="no-training-title">
          <SectionHeading>
            <NoTrainingTitle id="no-training-title">no-training guarantee</NoTrainingTitle>
            <NoTrainingBody>
              general AI models mix experiments with production data. prest0 keeps every prompt inside governed runtimes with full logging.
            </NoTrainingBody>
            <NoTrainingBody>
              <NoTrainingHighlight>no training</NoTrainingHighlight> means prompts, transcripts, and attachments never feed fine-tuning so you can scale AI without losing custody.
            </NoTrainingBody>
          </SectionHeading>
          <TableWrapper>
            <NoTrainingTable>
              <thead>
                <tr>
                  <th scope="col">general AI models</th>
                  <th scope="col">prest0</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td data-label="general AI models">training usually stays on; opt-outs hide behind settings.</td>
                  <td data-label="prest0"><strong>contractual no-training guarantee</strong> with dedicated enterprise AI endpoints.</td>
                </tr>
                <tr>
                  <td data-label="general AI models">open-ended retention for analytics and model updates.</td>
                  <td data-label="prest0">documented retention schedules, and deletion on request across storage and transcripts.</td>
                </tr>
                <tr>
                  <td data-label="general AI models">consumer terms reuse data and limit protection.</td>
                  <td data-label="prest0">MSA-backed terms with DPAs, SOC reporting, and signed riders.</td>
                </tr>
                <tr>
                  <td data-label="general AI models">no formal audit trail or scoped access.</td>
                  <td data-label="prest0">full audit logging, scoped access, and retention guardrails.</td>
                </tr>
                <tr>
                  <td data-label="general AI models">data may be shared with vendors to build features.</td>
                  <td data-label="prest0">third-party disclosures stay pre-approved, logged, and limited to signed sub-processors.</td>
                </tr>
              </tbody>
            </NoTrainingTable>
          </TableWrapper>
        </NoTrainingSection>

        {/* Compliance Comparison Table */}
        <Section id="compliance" aria-labelledby="compliance-table-title">
          <SectionHeading>
            <ComplianceTitle id="compliance-table-title">compliance comparison</ComplianceTitle>
            <ComplianceBody>see how each baseline requirement maps to the controls we run in production.</ComplianceBody>
          </SectionHeading>
          <TableWrapper>
            <ComparisonTable>
              <thead>
                <tr>
                  <th scope="col">requirement</th>
                  <th scope="col">baseline minimum</th>
                  <th scope="col">our implementation</th>
                  <th scope="col">status</th>
                </tr>
              </thead>
              <tbody>
                {complianceRows.map((row) => (
                  <tr key={row.requirement}>
                    <td data-label="requirement">{row.requirement}</td>
                    <td data-label="baseline minimum">{row.minimum}</td>
                    <td data-label="our implementation">{row.implementation}</td>
                    <td data-label="status">
                      <StatusBadge>
                        <CheckCircle2 size={16} />
                        {row.status.replace('✅ ', '')}
                      </StatusBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </ComparisonTable>
          </TableWrapper>
        </Section>

        {/* FAQ Accordion Section */}
        <Section id="faq" aria-labelledby="faq-title">
          <SectionHeading>
            <Title id="faq-title">faq</Title>
          </SectionHeading>
          {faqGroups.map((group) => (
            <FaqGroup key={group.id}>
              <FaqGroupTitle>{group.title}</FaqGroupTitle>
              {group.items.map((item) => (
                <AccordionItem key={item.id} $open={openFaqId === item.id}>
                  <button type="button" aria-expanded={openFaqId === item.id} onClick={() => setOpenFaqId((prev) => (prev === item.id ? null : item.id))}>
                    {item.q}
                    <HelpCircle size={20} />
                  </button>
                  <p>{item.a}</p>
                </AccordionItem>
              ))}
            </FaqGroup>
          ))}
        </Section>

        {/* Resources Section */}
        <Section id="contact" aria-labelledby="contact-title">
          <SectionHeading>
            <Title id="contact-title">resources</Title>
          </SectionHeading>
          <ContactCard>
            <ContactRow>
              <ContactLink href="/enterprise-security-architecture-trust-framework-white-paper.pdf" download>
                <FileText size={18} />
                download security whitepaper
              </ContactLink>
              <ContactLink href="/Prest0_DPA_Framework.pdf" download>
                <FileText size={18} />
                review data processing agreement
              </ContactLink>
            </ContactRow>
          </ContactCard>
        </Section>
          </MainContent>
        </SecurityLayout>
      </PageWrapper>
      <GlobalFooter />
      {/* PromptBar removed — GlobalChatSystem will be added later */}
    </>
  );
};

export default Security;

